import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LanguageService, SupportedLanguage } from '../../services/language.service';
import { CurrentUserService } from '../../services/current-user.service';
import { Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';

@Component({
  selector: 'app-language-switcher',
  standalone: true,
  imports: [CommonModule, Menu],
  templateUrl: './language-switcher.component.html',
  styleUrls: ['./language-switcher.component.scss']
})
export class LanguageSwitcherComponent implements OnInit, OnDestroy {
  currentLanguage: SupportedLanguage;
  supportedLanguages: { code: SupportedLanguage; name: string }[];
  private userSubscription?: Subscription;
  languageItems: MenuItem[] = []

  constructor(
    private languageService: LanguageService,
    private currentUserService: CurrentUserService
  ) {
    // Initialize with URL-based detection as fallback
    this.currentLanguage = this.languageService.getCurrentLanguage();
    this.supportedLanguages = this.languageService.getSupportedLanguages();
    this.updateLanguageItems();
  }

  ngOnInit(): void {
    // Update current language based on URL/port detection
    this.currentLanguage = this.languageService.getCurrentLanguage();

    // Subscribe to user data to get the actual language preference
    this.userSubscription = this.currentUserService.currentUser.pipe(
      filter(userData => !!userData?.currentUser),
      map(userData => {
        const user = userData!.currentUser!;
        return (user as any).settings?.language as SupportedLanguage;
      })
    ).subscribe(userLanguage => {
      if (userLanguage && this.supportedLanguages.find(lang => lang.code === userLanguage)) {
        // Only update if different from current URL-detected language
        const urlDetectedLang = this.languageService.getCurrentLanguage();
        if (userLanguage !== urlDetectedLang) {
          this.currentLanguage = userLanguage;
        }
        this.updateLanguageItems();
      }
    });
  }

  private updateLanguageItems(): void {
    this.languageItems = [
      {
        label: $localize`:i18n|List of supported languages header:Languages`,
        items: this.supportedLanguages.flatMap((supportedLanguage): MenuItem => {
          return {
            label: supportedLanguage.name,
            command: () => { this.languageService.switchLanguage(supportedLanguage.code); },
          }
        })
      }
    ]
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  getCurrentLanguageName(): string {
    const current = this.supportedLanguages.find(lang => lang.code === this.currentLanguage);
    return current?.name || 'Language';
  }
}